// Libs
import classNames from 'classnames/bind';
import { Button, DateInput, Flex, Heading, Table, TextInput, View, Tooltip, Text, Spinner, IconButton, IconEditLine, IconTrashLine } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BasePagination, BaseSelect } from '~/components/common';
// Others
import { ITuitionForm } from '~/utils/mockData/common';
import { DATE_FORMAT, DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE, TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { getStudents } from '~/thunks/user/userThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { defaultValuesForm, schema } from './helper';
import { createTuition, getListTuitions, getTuitionById, updateTuition } from '~/thunks/tuition/tuitionThunk';
import { IBodyCreateTuition, IBodyUpdateTuition, IQueryGetTuitions } from '~/utils/interface/tuition';
import { TuitionStatusEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './TuitionArea.module.scss';
import { icons } from '~/assets';
import { toastActions } from '~/thunks/toast/toastSlice';
import { generateGuid } from '~/utils/helper/common';
import { IToast } from '~/utils/interface/toast';
import ModalDeleteTuition from './component/ModalDeleteTuition';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const TuitionArea = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/tuition']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ITuitionForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { students } = useAppSelector((state) => state.userState);
  const { isRefreshListTuition, tuitions, isLoadingCreateTuition, isLoading, hasMore, currentPage } = useAppSelector(
    (state) => state.tuitionState
  );
  //#endregion Selector

  //#region Declare State
  const studentOptions =
    students?.responses.map((student) => ({
      value: String(student.id),
      label: student.fullName,
    })) ?? [];
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [editingTuitionId, setEditingTuitionId] = useState<string | null>(null);
  const [isLoadingUpdateTuition, setIsLoadingUpdateTuition] = useState(false);
  const [isLoadingEditTuition, setIsLoadingEditTuition] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tuitionIdToDelete, setTuitionIdToDelete] = useState<string | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetStudents();
    handleGetTuitions();
  }, []);

  useEffect(() => {
    if (isRefreshListTuition) {
      handleGetTuitions();
    }
  }, [isRefreshListTuition]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleEditTuition = async (tuitionId: string) => {
    setEditingTuitionId(tuitionId);
    setIsLoadingEditTuition(true);
    
    try {
      // Ensure students are loaded first
      if (studentOptions.length === 0) {
        await handleGetStudents();
      }

      // Fetch tuition data from API
      const res = await dispatch(getTuitionById(tuitionId)).unwrap();
      const tuition = res.data;
      
      if (!tuition) {
        setIsLoadingEditTuition(false);
        return;
      }

      // Set form values
      setValue('studentId', String(tuition.studentId));
      setValue('studentName', tuition.studentName);
      setValue('name', tuition.name);
      setValue('paidDate', tuition.paidDate || '');
    } catch (error) {
      setEditingTuitionId(null);
      console.error('Failed to load tuition:', error);
    } finally {
      setIsLoadingEditTuition(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTuitionId(null);
    setIsLoadingEditTuition(false);
    reset();
  };

  const handleUpdateTuition = async (tuitionId: string, payload: IBodyUpdateTuition) => {
    setIsLoadingUpdateTuition(true);
    await dispatch(updateTuition({ tuitionId, body: payload }))
      .unwrap()
      .then((res) => {
        // Show success toast
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_success_message', { ns: 'common/base' }),
          type: 'success',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);

        setEditingTuitionId(null);
        reset();
        handleGetTuitions();
      })
      .catch((error) => {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
      })
      .finally(() => {
        setIsLoadingUpdateTuition(false);
      });
  };

  const handleDeleteTuition = (tuitionId: string) => {
    setTuitionIdToDelete(tuitionId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTuitionIdToDelete(null);
  };

  const handleDeleteSuccess = () => {
    handleGetTuitions();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleGetStudents = async () => {
    await dispatch(
      getStudents({
        limit: DEFAULT_LIMIT,
      })
    ).unwrap();
  };

  const handleGetTuitions = async (query?: IQueryGetTuitions) => {
    await dispatch(
      getListTuitions(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    ).unwrap();
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetTuitions({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const handleCreateTuition = async (payload: IBodyCreateTuition) => {
    await dispatch(createTuition(payload))
      .unwrap()
      .then((res) => {
        reset();
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleSubmitForm = (data: ITuitionForm) => {
    if (!data) return;

    if (editingTuitionId) {
      // Update mode
      const payload: IBodyUpdateTuition = {
        studentId: data.studentId,
        studentName: data.studentName,
        name: data.name,
        paidDate: data.paidDate,
      };
      handleUpdateTuition(editingTuitionId, payload);
    } else {
      // Create mode
      const payload: IBodyCreateTuition = {
        name: data.name,
        studentId: data.studentId,
        studentName: data.studentName,
        paidDate: data.paidDate,
      };
      handleCreateTuition(payload);
    }
  };
  
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('tuitionTitle')}>
          <Heading level='h3'>{t('tuition_area_title', { ns: 'admin/tuition' })}</Heading>
          <img className={cx('tuitionIcon')} src={icons.commonIconTuitionArea} alt={t('common_alt_img')} />
        </View>

        <View as='div' className={cx('tuitionForm')} margin='medium 0' width='100%'>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <View as='div' className={cx('tuitionInputContainer')}>
              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='studentId'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_select_student_label', { ns: 'admin/tuition' })}
                      </label>
                      <BaseSelect
                        value={value}
                        options={studentOptions}
                        onChange={(e) => {
                          onChange(e.value);
                          setValue('studentName', e.label);
                        }}
                      />
                      {errors?.studentId?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.studentId?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>

              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='name'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_insert_tuition_name_label', { ns: 'admin/tuition' })}
                      </label>
                      <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
                      {errors?.name?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.name?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>

              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='paidDate'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_insert_tuition_status_date_label', { ns: 'admin/tuition' })}
                      </label>
                      <View width='100%'>
                        <DateInput
                          display='block'
                          renderLabel=''
                          value={value}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>, value: { value: string }) => {
                            if (value.value) {
                              const formattedDate = moment(value.value, DATE_FORMAT.LONG_FORMAT).format(
                                DATE_FORMAT.DATE
                              );
                              onChange(formattedDate);
                            }
                          }}
                        />
                      </View>
                      {errors?.paidDate?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.paidDate?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>
            </View>

            <Flex justifyItems='center' margin='small 0 0 0'>
              <View as='div' className={cx('tuitionButton')}>
                {editingTuitionId ? (
                  <Flex gap='small'>
                    <Button
                      color='secondary'
                      type='button'
                      onClick={handleCancelEdit}
                      disabled={isLoadingUpdateTuition || isLoadingCreateTuition || isLoadingEditTuition}
                    >
                      {t('cancel_button_label', { ns: 'common/base' })}
                    </Button>
                    <Button
                      color='primary'
                      type='submit'
                      disabled={isLoadingUpdateTuition || isLoadingCreateTuition || isLoadingEditTuition}
                      renderIcon={(isLoadingUpdateTuition || isLoadingCreateTuition || isLoadingEditTuition) && <Spinner size='x-small' />}
                    >
                      {t('save_button_label', { ns: 'common/base' })}
                    </Button>
                  </Flex>
                ) : (
                  <Button
                    type='submit'
                    color='primary'
                    disabled={isLoadingCreateTuition}
                    renderIcon={isLoadingCreateTuition && <Spinner size='x-small' />}
                  >
                    {t('tuition_area_add_tuition_label', { ns: 'admin/tuition' })}
                  </Button>
                )}
              </View>
            </Flex>
          </form>
        </View>

        <View as='div' className={cx('tuitionTableDesktop')}>
          <Table layout='auto'>
            <Table.Head></Table.Head>
            <Table.Body>
              {tuitions.map((item, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <div className={cx('tuitionTable')}>
                      <span>{item.studentId}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('tuitionTable')}>
                      <Tooltip renderTip={item.studentName}>
                        <span>{item.studentName}</span>
                      </Tooltip>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('tableCell')}>
                      <div className={cx('tuitionTable')}>
                        <img
                          src={icons.commonIconTuitionFees}
                          alt={t('common_alt_img')}
                          className={cx('tuitionFeeIcon')}
                        />
                        <Tooltip renderTip={item.name}>
                          <span>{item.name}</span>
                        </Tooltip>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('tableCell')}>
                      <div className={cx('tuitionTable')}>
                        <img
                          className={cx('iconCalendar')}
                          src={icons.commonIconCalendarDay}
                          alt={t('common_alt_img')}
                        />
                        <Tooltip
                          renderTip={
                            item.status === TuitionStatusEnum.PAID
                              ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                              : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })
                          }
                        >
                          <span>
                            {item.status === TuitionStatusEnum.PAID
                              ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                              : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('actionsCellWrapper')}>
                      <Flex 
                        gap='small' 
                        alignItems='center' 
                        style={{ 
                          justifyContent: 'flex-end',
                          marginRight: 0,
                          paddingRight: 0
                        }}
                      >
                        <IconButton
                          withBorder={false}
                          withBackground={false}
                          color='primary'
                          onClick={() => handleEditTuition(item.id)}
                          screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                        >
                          <IconEditLine />
                        </IconButton>
                        <IconButton
                          withBorder={false}
                          withBackground={false}
                          color='danger'
                          onClick={() => handleDeleteTuition(item.id)}
                          screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                        >
                          <IconTrashLine />
                        </IconButton>
                      </Flex>
                    </div>
                  </Table.Cell>
                
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>

        <View className={cx('tuitionTableMobile')}>
          {tuitions.map((item, index) => (
            <View
              key={index}
              as='div'
              className={cx('tuitionCard')}
              padding='small'
              borderWidth='small'
              borderRadius='medium'
            >
              <Heading level='h4' margin='0 0 small 0'>
                <Tooltip renderTip={item.studentId}>
                  <span>
                    {t('tuition_area_select_student_id_label', { ns: 'admin/tuition' })} {item.studentId}
                  </span>
                </Tooltip>
              </Heading>

              <Heading level='h4' margin='0 0 small 0'>
                <Tooltip renderTip={item.studentName}>
                  <span>{item.studentName}</span>
                </Tooltip>
              </Heading>

              <Flex gap='small' alignItems='center' margin='small 0'>
                <div className={cx('tuitionTable')}>
                  <img src={icons.commonIconTuitionFees} alt={t('common_alt_img')} className={cx('tuitionFeeIcon')} />
                  <Tooltip renderTip={item.name}>
                    <span>{item.name}</span>
                  </Tooltip>
                </div>
              </Flex>

              <Flex gap='small' alignItems='center' margin='small 0'>
                <div className={cx('tuitionTable')}>
                  <img className={cx('iconCalendar')} src={icons.commonIconCalendarDay} alt={t('common_alt_img')} />
                  <Tooltip
                    renderTip={
                      item.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                        : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })
                    }
                  >
                    <span>
                      {item.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                        : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })}
                    </span>
                  </Tooltip>
                </div>
              </Flex>

              <Flex gap='small' alignItems='center' margin='small 0'>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='primary'
                  onClick={() => handleEditTuition(item.id)}
                  screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                >
                  <IconEditLine />
                </IconButton>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='danger'
                  onClick={() => handleDeleteTuition(item.id)}
                  screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                >
                  <IconTrashLine />
                </IconButton>
              </Flex>
            </View>
          ))}

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>
      </div>

      <ModalDeleteTuition
        isModalOpen={isDeleteModalOpen}
        tuitionId={tuitionIdToDelete}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default TuitionArea;
